import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PacketSession } from '@theblob/ts-core/bproto';
import * as TbmProto from '@theblob/protocol-tbm';
import type { TbmEvent } from '../domain/types';
import { keys } from '../api/queryKeys';

const {
  PublicOpcode,
  PubSubscribeCodec,
  PubUnsubscribeCodec,
  PubHelloCodec,
  PubEventCodec,
  PubErrorCodec,
  PubHistoryCodec,
  PubHistoryResultCodec,
  PubEmptyCodec,
  PubSubscribedCodec,
  TBM_PUBLIC_SOFTWARE,
  enc,
  dec,
  emptyExtFields,
} = TbmProto;

const PING_MS = 15000;
const STALE_MS = 40000;
const RECONNECT_MS = 3000;

export type SocketStatus = 'idle' | 'connecting' | 'open' | 'closed';

export interface EventFilters {
  botId?: string;
  broadcasterUserId?: string;
  types?: string[];
}

export function eventSocketUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/api/v2/ws`;
}

function toBytes(data: Blob | ArrayBuffer): Promise<Uint8Array> | Uint8Array {
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  return data.arrayBuffer().then((buf) => new Uint8Array(buf));
}

export function useEventStream(options: { enabled: boolean; filters: EventFilters; limit?: number }): {
  status: SocketStatus;
  error: string | null;
  events: TbmEvent[];
  clear: () => void;
} {
  const { enabled, limit = 300 } = options;
  const botId = options.filters.botId ?? '';
  const broadcasterUserId = options.filters.broadcasterUserId ?? '';
  const types = (options.filters.types ?? []).join(',');
  const qc = useQueryClient();

  const [status, setStatus] = useState<SocketStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<TbmEvent[]>([]);
  const lastEventId = useRef('');
  const subscriptionId = useRef('');

  const clear = useCallback(() => {
    setEvents([]);
    lastEventId.current = '';
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return undefined;
    }

    let socket: WebSocket | undefined;
    let session: PacketSession | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;
    let disposed = false;
    let lastPong = Date.now();

    const send = (bytes: Uint8Array): void => {
      if (socket?.readyState === WebSocket.OPEN) socket.send(bytes);
    };

    const encode = (opcode: number, payload: Uint8Array): void => {
      if (!session) return;
      send(session.encodeRequest(opcode, payload).bytes);
    };

    const pushEvent = (event: TbmEvent): void => {
      lastEventId.current = event.id;
      setEvents((current) => [event, ...current.filter((e) => e.id !== event.id)].slice(0, limit));
      if (event.type === 'tbm.reconcile.completed') {
        void qc.invalidateQueries({ queryKey: keys.bots });
        void qc.invalidateQueries({ queryKey: keys.eventsub });
      }
    };

    const subscribe = (): void => {
      if (!session) return;
      encode(
        PublicOpcode.SUBSCRIBE,
        enc(PubSubscribeCodec, {
          types: types ? types.split(',') : [],
          botId,
          broadcasterUserId,
          extensions: emptyExtFields(),
        }),
      );
      encode(
        PublicOpcode.HISTORY,
        enc(PubHistoryCodec, {
          lastEventId: lastEventId.current,
          types: types ? types.split(',') : [],
          botId,
          broadcasterUserId,
          limit: 50,
          extensions: emptyExtFields(),
        }),
      );
    };

    const connect = (): void => {
      if (disposed) return;
      setStatus('connecting');
      try {
        socket = new WebSocket(eventSocketUrl());
        socket.binaryType = 'arraybuffer';
      } catch (e) {
        setError(String(e));
        setStatus('closed');
        return;
      }
      session = new PacketSession({
        role: 'client',
        localHello: { softwareName: TBM_PUBLIC_SOFTWARE, softwareVersion: '1.0.0' },
        knownOpcodes: new Set(Object.values(PublicOpcode)),
      });

      socket.onopen = () => {
        setError(null);
        lastPong = Date.now();
        if (session) send(session.beginHandshake());
      };

      socket.onmessage = (raw) => {
        void (async () => {
          if (!session) return;
          const data = raw.data;
          const buf = data instanceof ArrayBuffer || data instanceof Blob ? await toBytes(data) : null;
          if (!buf) return;
          let packets;
          try {
            packets = session.push(buf);
            const pending = session.takePendingOutbound();
            if (pending) send(pending);
          } catch {
            return;
          }
          for (const packet of packets) {
            switch (packet.opcode) {
              case PublicOpcode.HELLO: {
                try {
                  const hello = dec(PubHelloCodec, packet.payload);
                  const authByte = hello.extensions.find((e) => e.fieldId === 1)?.value[0];
                  if (authByte === 0) {
                    setError('Socket non authentifiée. Reconnectez-vous.');
                    return;
                  }
                } catch {
                  /* ignore */
                }
                setStatus('open');
                subscribe();
                break;
              }
              case PublicOpcode.AUTHENTICATED:
                setStatus('open');
                subscribe();
                break;
              case PublicOpcode.SUBSCRIBED:
                try {
                  subscriptionId.current = dec(PubSubscribedCodec, packet.payload).subscriptionId;
                } catch {
                  /* ignore */
                }
                break;
              case PublicOpcode.PONG:
                lastPong = Date.now();
                break;
              case PublicOpcode.HISTORY_RESULT: {
                try {
                  const result = dec(PubHistoryResultCodec, packet.payload);
                  const parsed = JSON.parse(result.eventsJson) as TbmEvent[];
                  if (Array.isArray(parsed)) parsed.forEach(pushEvent);
                } catch {
                  /* ignore */
                }
                break;
              }
              case PublicOpcode.EVENT: {
                try {
                  const ev = dec(PubEventCodec, packet.payload);
                  let payload: unknown = {};
                  try {
                    payload = JSON.parse(ev.payloadJson);
                  } catch {
                    payload = ev.payloadJson;
                  }
                  pushEvent({
                    id: ev.id,
                    type: ev.type,
                    version: ev.version,
                    botId: ev.botId,
                    broadcasterUserId: ev.broadcasterUserId,
                    occurredAt: ev.occurredAt,
                    payload,
                  });
                } catch {
                  /* ignore */
                }
                break;
              }
              case PublicOpcode.ERROR: {
                try {
                  const err = dec(PubErrorCodec, packet.payload);
                  setError(err.message || err.code || 'erreur socket');
                } catch {
                  setError('erreur socket');
                }
                break;
              }
              default:
            }
          }
        })();
      };

      socket.onclose = () => {
        if (disposed) return;
        setStatus('closed');
        retry = setTimeout(connect, RECONNECT_MS);
      };
    };

    ping = setInterval(() => {
      if (socket?.readyState !== WebSocket.OPEN || !session) return;
      if (Date.now() - lastPong > STALE_MS) {
        socket.close();
        return;
      }
      encode(PublicOpcode.PING, enc(PubEmptyCodec, { extensions: emptyExtFields() }));
    }, PING_MS);

    connect();
    return () => {
      disposed = true;
      clearTimeout(retry);
      clearInterval(ping);
      if (session && subscriptionId.current) {
        try {
          encode(
            PublicOpcode.UNSUBSCRIBE,
            enc(PubUnsubscribeCodec, { subscriptionId: subscriptionId.current, extensions: emptyExtFields() }),
          );
        } catch {
          /* ignore */
        }
      }
      socket?.close();
      setStatus('idle');
    };
  }, [enabled, botId, broadcasterUserId, types, limit, qc]);

  return { status, error, events, clear };
}
